'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { TabEnum } from '../../constants/TabConfig';
import { SCREENS, SUBSCREENS } from '../../constants/Tracking';
import { useAnchorLinkScroll } from '../../utils/customHooks';
import { useQueryParamsEffect } from '../../utils/queryParamUtils';
import { useQueryParamTerm } from '../../utils/stateUtils';
import CurrencyPicker from '../common/CurrencyPicker';
import HubInfoContainer from '../common/HubInfoContainer';
import PageContainer from '../common/PageContainer';
import StyledBlob from '../common/StyledBlob';
import TabLayout from '../tabs/TabLayout';
import StandardProductPageTitle from './StandardProductPageTitle';
import { OPERATIONS } from 'self-service-api/constants/ProductLines';
import HubSubtitle from '../common/HubSubtitle';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CalculatorAnchorLink from '../common/CalculatorAnchorLink';
import BannerSection from '../banners/BannerSection';
import { useSelector } from 'react-redux';
import FailedLoadBanner from '../loading/FailedLoadBanner';
import FallbackErrorBanner from '../loading/FallbackErrorBanner';
import HubTierInfo from './HubTierInfo';
import RecommendedBundlesSection from './bundles-section/RecommendedBundlesSection';
import PhoneSupportWrapper from '../common/phone-numbers/PhoneSupportWrapper';
import PhoneSupport from '../common/PhoneSupport';
import CustomerSupport from '../common/CustomerSupport';
import HubSpotPayments from 'pricing-pages-unified-ui/components/common/HubSpotPayments';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import { withDocumentTitle } from '../../utils/DocumentUtils';
import { hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import Faq from '../common/Faq';
import Question from '../common/Question';
import { getCommunityUrlPublic, getOperationsUrlPublic, getAcademyUrlPublic, getKnowledgeBaseUrlPublic, getLanguagesUrlPublic, getOperationsSupportedApplicationsUrl, getStartupsUrlPublic, getNonprofitsUrlPublic, PAYMENTS_LANDING_PAGE_URL } from 'pricing-pages-lib/utils/publicPageUrls';
import FooterMicrocopy from '../common/FooterMicrocopy';
import { HubCalculator } from '../common/Calculator';
import { useTrackRetail } from '../../selectors/RetailSelectors';
import FreeCard from '../common/FreeCard';
import { getHighlightsForProductLine } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { useFeatureStore } from '../../hooks/useFeatureStore';

const OperationsHubPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: SCREENS.PRODUCTS_PLANS,
      subscreen: SUBSCREENS.OPERATIONS_HUB
    });
  }, []);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const highlightedProducts = getHighlightsForProductLine(OPERATIONS);
  useFeatureStore({
    highlightedProducts,
    productLine: OPERATIONS
  });
  return /*#__PURE__*/_jsx(TabLayout, {
    selectedPrimaryTab: TabEnum.PRODUCTS,
    selectedSecondaryTab: TabEnum.OPERATIONS,
    children: /*#__PURE__*/_jsxs(PageContainer, {
      "data-selenium-id": "page-success",
      children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
        name: "OPERATIONS_PAGE"
      }), /*#__PURE__*/_jsx(CurrencyPicker, {}), /*#__PURE__*/_jsx(StyledBlob, {}), /*#__PURE__*/_jsxs(HubInfoContainer, {
        children: [/*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsx(StandardProductPageTitle, {
          productLine: OPERATIONS,
          includeRegisteredTrademark: true
        }), /*#__PURE__*/_jsxs(HubSubtitle, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricing-pages.hubs.operations.description"
          }), /*#__PURE__*/_jsx(CalculatorAnchorLink, {})]
        }), /*#__PURE__*/_jsx(BannerSection, {
          productLine: OPERATIONS,
          screen: SCREENS.PRODUCTS_PLANS,
          subscreen: SUBSCREENS.OPERATIONS_HUB
        }), /*#__PURE__*/_jsx(HubTierInfo, {
          productLine: OPERATIONS
        }), /*#__PURE__*/_jsx(FreeCard, {
          productLine: OPERATIONS
        }), /*#__PURE__*/_jsx(RecommendedBundlesSection, {
          productLine: OPERATIONS
        }), /*#__PURE__*/_jsx(PhoneSupportWrapper, {
          children: /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: "operations-middle",
            screen: SUBSCREENS.OPERATIONS_HUB
          })
        }), /*#__PURE__*/_jsx(HubSpotPayments, {}), /*#__PURE__*/_jsx(HubCalculator, {
          productLine: OPERATIONS,
          screen: SUBSCREENS.OPERATIONS_HUB
        }), /*#__PURE__*/_jsx(CustomerSupport, {}), /*#__PURE__*/_jsxs(Faq, {
          children: [/*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatIsOperationsHub",
            href: getOperationsUrlPublic()
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatIsPayments411",
            href: PAYMENTS_LANDING_PAGE_URL
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatSupport",
            passedInOptions: {
              communityHref: getCommunityUrlPublic(),
              academyHref: getAcademyUrlPublic(),
              kbHref: getKnowledgeBaseUrlPublic(),
              langHref: getLanguagesUrlPublic(),
              onLinkClick: url => {
                getUsageTracker().track('externalLinkClick', {
                  name: 'faq-click-link',
                  url
                });
              }
            }
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatDoesDataSyncDo",
            isJSX: false
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatTypeOfDataSync",
            href: getOperationsSupportedApplicationsUrl()
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatApplicationsSync",
            href: getOperationsSupportedApplicationsUrl()
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatIsProgrammableAutomation",
            isJSX: false
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "operations.whatDiscounts411",
            passedInOptions: {
              startupsHref: getStartupsUrlPublic('pricing-operationspage'),
              nonprofitsHref: getNonprofitsUrlPublic('pricing-operationspage'),
              onLinkClick: url => {
                getUsageTracker().track('externalLinkClick', {
                  name: 'faq-click-link',
                  url
                });
              }
            }
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "operations-footer",
          screen: SUBSCREENS.OPERATIONS_HUB
        })]
      })]
    })
  });
};

export default withDocumentTitle(OperationsHubPage, 'operations');